import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Header from '../components/Header';
import { 
  Shield, Search, Package, Plus, Edit, Trash2, Save, X, 
  Tag, Settings, BarChart3, RefreshCw, Check, AlertCircle,
  DollarSign, Percent, Home, Eye, EyeOff, ArrowUpDown, LogOut, Users
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to get auth headers
const getAuthHeaders = () => {
  const token = localStorage.getItem('admin_token');
  return {
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${token}`
  };
};

// Helper to safely parse JSON response, handling "body already consumed" error
const safeJsonParse = async (response) => {
  try {
    return await response.json();
  } catch (e) {
    console.warn('JSON parse failed, response may have been consumed:', e.message);
    return null;
  }
};

// Helper to fetch with timeout and retry
const fetchWithTimeout = async (url, options = {}, timeout = 20000, retries = 2) => {
  let lastError;
  
  for (let attempt = 0; attempt <= retries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), timeout);
    
    try {
      const response = await fetch(url, {
        ...options,
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      lastError = error;
      
      if (error.name === 'AbortError') {
        console.warn(`Timeout on attempt ${attempt + 1} for ${url}`);
      } else {
        console.warn(`Fetch error on attempt ${attempt + 1} for ${url}:`, error.message);
      }
      
      // Wait before retry (exponential backoff)
      if (attempt < retries) {
        await new Promise(resolve => setTimeout(resolve, 1000 * (attempt + 1)));
      }
    }
  }
  
  if (lastError?.name === 'AbortError') {
    throw new Error('Timeout - il server non risponde dopo più tentativi');
  }
  throw lastError;
};

const AdminPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('services');
  const [services, setServices] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState({ vat_rate: 22, bundle_discount_percent: 5 });
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [currentUser, setCurrentUser] = useState(null);
  
  // Edit modals
  const [editingService, setEditingService] = useState(null);
  const [editingCoupon, setEditingCoupon] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [showNewService, setShowNewService] = useState(false);
  const [showNewCoupon, setShowNewCoupon] = useState(false);
  const [showNewUser, setShowNewUser] = useState(false);

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem('admin_token');
    const user = localStorage.getItem('admin_user');
    
    if (!token) {
      navigate('/login');
      return;
    }
    
    if (user) {
      setCurrentUser(JSON.parse(user));
    }
    
    loadData();
  }, [navigate]);

  const handleLogout = () => {
    localStorage.removeItem('admin_token');
    localStorage.removeItem('admin_user');
    navigate('/login');
  };

  const loadData = async () => {
    setLoading(true);
    setError('');
    
    try {
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        console.error('No token found in localStorage');
        setError('Sessione scaduta. Effettua nuovamente il login.');
        setTimeout(() => navigate('/login'), 2000);
        return;
      }
      
      const headers = getAuthHeaders();
      console.log('Loading admin data with token:', token.substring(0, 20) + '...');
      
      // Fetch data SEQUENTIALLY to avoid Safari parallel request issues
      const failedEndpoints = [];
      let servicesData = { services: [] };
      let couponsData = { coupons: [] };
      let settingsData = { settings: {} };
      let statsData = { stats: null };
      let usersData = { users: [] };
      
      // 1. Services
      try {
        console.log('Fetching services...');
        const res = await fetchWithTimeout(`${API_URL}/api/admin/services`, { headers });
        if (res.status === 401) {
          throw new Error('UNAUTHORIZED');
        }
        if (res.ok) {
          const data = await safeJsonParse(res);
          if (data) {
            servicesData = data;
            console.log('Services loaded:', servicesData.services?.length);
          } else {
            failedEndpoints.push('Servizi');
          }
        } else {
          failedEndpoints.push('Servizi');
        }
      } catch (e) {
        if (e.message === 'UNAUTHORIZED') {
          setError('Sessione scaduta. Effettua nuovamente il login.');
          setTimeout(() => {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
            navigate('/login');
          }, 2000);
          return;
        }
        console.error('Services error:', e);
        failedEndpoints.push('Servizi');
      }
      
      // 2. Coupons
      try {
        console.log('Fetching coupons...');
        const res = await fetchWithTimeout(`${API_URL}/api/admin/coupons`, { headers });
        if (res.status === 401) {
          throw new Error('UNAUTHORIZED');
        }
        if (res.ok) {
          const data = await safeJsonParse(res);
          if (data) {
            couponsData = data;
            console.log('Coupons loaded:', couponsData.coupons?.length);
          } else {
            failedEndpoints.push('Coupon');
          }
        } else {
          failedEndpoints.push('Coupon');
        }
      } catch (e) {
        if (e.message === 'UNAUTHORIZED') {
          setError('Sessione scaduta. Effettua nuovamente il login.');
          setTimeout(() => {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
            navigate('/login');
          }, 2000);
          return;
        }
        console.error('Coupons error:', e);
        failedEndpoints.push('Coupon');
      }
      
      // 3. Settings
      try {
        console.log('Fetching settings...');
        const res = await fetchWithTimeout(`${API_URL}/api/admin/settings`, { headers });
        if (res.status === 401) {
          throw new Error('UNAUTHORIZED');
        }
        if (res.ok) {
          const data = await safeJsonParse(res);
          if (data) {
            settingsData = data;
            console.log('Settings loaded');
          } else {
            failedEndpoints.push('Impostazioni');
          }
        } else {
          failedEndpoints.push('Impostazioni');
        }
      } catch (e) {
        if (e.message === 'UNAUTHORIZED') {
          setError('Sessione scaduta. Effettua nuovamente il login.');
          setTimeout(() => {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
            navigate('/login');
          }, 2000);
          return;
        }
        console.error('Settings error:', e);
        failedEndpoints.push('Impostazioni');
      }
      
      // 4. Stats
      try {
        console.log('Fetching stats...');
        const res = await fetchWithTimeout(`${API_URL}/api/admin/stats`, { headers });
        if (res.status === 401) {
          throw new Error('UNAUTHORIZED');
        }
        if (res.ok) {
          const data = await safeJsonParse(res);
          if (data) {
            statsData = data;
            console.log('Stats loaded');
          } else {
            failedEndpoints.push('Statistiche');
          }
        } else {
          failedEndpoints.push('Statistiche');
        }
      } catch (e) {
        if (e.message === 'UNAUTHORIZED') {
          setError('Sessione scaduta. Effettua nuovamente il login.');
          setTimeout(() => {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
            navigate('/login');
          }, 2000);
          return;
        }
        console.error('Stats error:', e);
        failedEndpoints.push('Statistiche');
      }
      
      // 5. Users
      try {
        console.log('Fetching users...');
        const res = await fetchWithTimeout(`${API_URL}/api/admin/users`, { headers });
        if (res.status === 401) {
          throw new Error('UNAUTHORIZED');
        }
        if (res.ok) {
          const data = await safeJsonParse(res);
          if (data) {
            usersData = data;
            console.log('Users loaded:', usersData.users?.length);
          } else {
            failedEndpoints.push('Utenti');
          }
        } else {
          failedEndpoints.push('Utenti');
        }
      } catch (e) {
        if (e.message === 'UNAUTHORIZED') {
          setError('Sessione scaduta. Effettua nuovamente il login.');
          setTimeout(() => {
            localStorage.removeItem('admin_token');
            localStorage.removeItem('admin_user');
            navigate('/login');
          }, 2000);
          return;
        }
        console.error('Users error:', e);
        failedEndpoints.push('Utenti');
      }
      
      // Set state
      setServices(servicesData.services || []);
      setCoupons(couponsData.coupons || []);
      setSettings(settingsData.settings || {});
      setStats(statsData.stats || null);
      setUsers(usersData.users || []);
      
      // Show error if some data failed
      if (failedEndpoints.length > 0 && failedEndpoints.length < 5) {
        setError(`Non caricati: ${failedEndpoints.join(', ')}. Premi Riprova.`);
      } else if (failedEndpoints.length === 5) {
        setError('Impossibile caricare i dati. Verifica la connessione e riprova.');
      }
      
    } catch (err) {
      console.error('LoadData error:', err);
      setError(`Errore nel caricamento: ${err.message || 'Errore di rete'}`);
    }
    setLoading(false);
  };

  const showMessage = (msg, isError = false) => {
    if (isError) {
      setError(msg);
      setTimeout(() => setError(''), 3000);
    } else {
      setSuccess(msg);
      setTimeout(() => setSuccess(''), 3000);
    }
  };

  // Service handlers
  const handleSaveService = async (service, isNew = false) => {
    try {
      const url = isNew 
        ? `${API_URL}/api/admin/services`
        : `${API_URL}/api/admin/services/${service.id}`;
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(service)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore');
      }
      
      showMessage(isNew ? 'Servizio creato!' : 'Servizio aggiornato!');
      setEditingService(null);
      setShowNewService(false);
      loadData();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Disattivare questo servizio?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/services/${serviceId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!res.ok) throw new Error('Errore eliminazione');
      
      showMessage('Servizio disattivato!');
      loadData();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // Coupon handlers
  const handleSaveCoupon = async (coupon, isNew = false) => {
    try {
      const url = isNew 
        ? `${API_URL}/api/admin/coupons`
        : `${API_URL}/api/admin/coupons/${coupon.code}`;
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(coupon)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore');
      }
      
      showMessage(isNew ? 'Coupon creato!' : 'Coupon aggiornato!');
      setEditingCoupon(null);
      setShowNewCoupon(false);
      loadData();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleDeleteCoupon = async (code) => {
    if (!window.confirm('Eliminare questo coupon?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/coupons/${code}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!res.ok) throw new Error('Errore eliminazione');
      
      showMessage('Coupon eliminato!');
      loadData();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // User handlers
  const handleSaveUser = async (user, isNew = false) => {
    try {
      const url = isNew 
        ? `${API_URL}/api/admin/users`
        : `${API_URL}/api/admin/users/${user.username}`;
      
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(user)
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore');
      }
      
      showMessage(isNew ? 'Utente creato!' : 'Utente aggiornato!');
      setEditingUser(null);
      setShowNewUser(false);
      loadData();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const handleDeleteUser = async (username) => {
    if (!window.confirm('Eliminare questo utente?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/admin/users/${username}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || 'Errore');
      }
      
      showMessage('Utente eliminato!');
      loadData();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  // Settings handler
  const handleSaveSettings = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings)
      });
      
      if (!res.ok) throw new Error('Errore salvataggio');
      
      showMessage('Impostazioni salvate!');
      loadData();
    } catch (err) {
      showMessage(err.message, true);
    }
  };

  const iconOptions = ['Shield', 'Search', 'Package', 'Lock', 'Globe', 'Server', 'Zap', 'Eye'];
  const colorOptions = [
    'from-red-500 to-orange-500',
    'from-cyan-500 to-blue-500',
    'from-purple-500 to-pink-500',
    'from-green-500 to-emerald-500',
    'from-yellow-500 to-orange-500',
    'from-indigo-500 to-purple-500'
  ];

  return (
    <div className="min-h-screen bg-[#0f0f1a]">
      <Header showGuide={false} />
      
      <main className="pt-40 pb-16 px-4">
        <div className="max-w-6xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-white">Pannello Admin</h1>
              <p className="text-gray-400">
                Benvenuto, <span className="text-cyan-400">{currentUser?.username || 'Admin'}</span>
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={loadData}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2"
              >
                <RefreshCw className="h-4 w-4" /> Aggiorna
              </button>
              <Link
                to="/"
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg flex items-center gap-2"
              >
                <Home className="h-4 w-4" /> Home
              </Link>
              <button
                onClick={handleLogout}
                className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
              >
                <LogOut className="h-4 w-4" /> Esci
              </button>
            </div>
          </div>

          {/* Messages */}
          {error && (
            <div className="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-lg text-red-400 flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <AlertCircle className="h-5 w-5 flex-shrink-0" /> 
                <span>{error}</span>
              </div>
              <button 
                onClick={loadData}
                className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white text-sm rounded-lg flex items-center gap-1"
              >
                <RefreshCw className="h-4 w-4" /> Riprova
              </button>
            </div>
          )}
          {success && (
            <div className="mb-4 p-4 bg-green-500/20 border border-green-500/50 rounded-lg text-green-400 flex items-center gap-2">
              <Check className="h-5 w-5" /> {success}
            </div>
          )}

          {/* Tabs */}
          <div className="flex gap-2 mb-6 border-b border-gray-800 pb-4 flex-wrap">
            {[
              { id: 'services', label: 'Servizi', icon: Package },
              { id: 'coupons', label: 'Coupon', icon: Tag },
              { id: 'users', label: 'Utenti', icon: Users },
              { id: 'settings', label: 'Impostazioni', icon: Settings },
              { id: 'stats', label: 'Statistiche', icon: BarChart3 }
            ].map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg flex items-center gap-2 transition-all ${
                  activeTab === tab.id 
                    ? 'bg-red-600 text-white' 
                    : 'bg-gray-800 text-gray-400 hover:bg-gray-700'
                }`}
              >
                <tab.icon className="h-4 w-4" /> {tab.label}
              </button>
            ))}
          </div>

          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 text-cyan-400 animate-spin mx-auto mb-4" />
              <p className="text-gray-400">Caricamento...</p>
            </div>
          ) : (
            <>
              {/* Services Tab */}
              {activeTab === 'services' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">Servizi</h2>
                    <button
                      onClick={() => setShowNewService(true)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" /> Nuovo Servizio
                    </button>
                  </div>
                  
                  <div className="grid gap-4">
                    {services.map(service => (
                      <div
                        key={service.id}
                        className={`bg-[#1a1a2e] rounded-xl p-4 border ${service.active ? 'border-gray-800' : 'border-red-500/30 opacity-60'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <div className={`p-2 rounded-lg bg-gradient-to-r ${service.color}`}>
                                {service.icon === 'Shield' && <Shield className="h-5 w-5 text-white" />}
                                {service.icon === 'Search' && <Search className="h-5 w-5 text-white" />}
                                {service.icon === 'Package' && <Package className="h-5 w-5 text-white" />}
                              </div>
                              <div>
                                <h3 className="font-bold text-white">{service.name}</h3>
                                <p className="text-sm text-gray-400">{service.id}</p>
                              </div>
                              {service.is_bundle && (
                                <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">BUNDLE</span>
                              )}
                              {!service.active && (
                                <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">DISATTIVO</span>
                              )}
                            </div>
                            <p className="text-sm text-gray-400 mb-2">{service.description}</p>
                            <div className="flex items-center gap-4 text-sm">
                              <span className="text-white font-bold">€{service.price_gross}</span>
                              <span className="text-gray-500">(€{service.price_net} + IVA {service.vat_rate}%)</span>
                              {service.is_bundle && service.savings && (
                                <span className="text-green-400">Risparmio: €{service.savings}</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-2">
                            <button
                              onClick={() => setEditingService(service)}
                              className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
                            >
                              <Edit className="h-4 w-4 text-cyan-400" />
                            </button>
                            <button
                              onClick={() => handleDeleteService(service.id)}
                              className="p-2 bg-gray-800 hover:bg-red-900 rounded-lg"
                            >
                              {service.active ? <EyeOff className="h-4 w-4 text-red-400" /> : <Eye className="h-4 w-4 text-green-400" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Coupons Tab */}
              {activeTab === 'coupons' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">Coupon</h2>
                    <button
                      onClick={() => setShowNewCoupon(true)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" /> Nuovo Coupon
                    </button>
                  </div>
                  
                  <div className="grid gap-3">
                    {coupons.map(coupon => (
                      <div
                        key={coupon.code}
                        className={`bg-[#1a1a2e] rounded-xl p-4 border ${coupon.active ? 'border-gray-800' : 'border-red-500/30 opacity-60'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-yellow-500/20 rounded-lg">
                              <Tag className="h-5 w-5 text-yellow-400" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white font-mono">{coupon.code}</h3>
                              <p className="text-sm text-gray-400">{coupon.description}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-4">
                            <span className="text-2xl font-bold text-green-400">-{coupon.discount_percent}%</span>
                            {!coupon.active && (
                              <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">DISATTIVO</span>
                            )}
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingCoupon(coupon)}
                                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
                              >
                                <Edit className="h-4 w-4 text-cyan-400" />
                              </button>
                              <button
                                onClick={() => handleDeleteCoupon(coupon.code)}
                                className="p-2 bg-gray-800 hover:bg-red-900 rounded-lg"
                              >
                                <Trash2 className="h-4 w-4 text-red-400" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Settings Tab */}
              {activeTab === 'settings' && (
                <div className="bg-[#1a1a2e] rounded-xl p-6 border border-gray-800">
                  <h2 className="text-xl font-bold text-white mb-6">Impostazioni Globali</h2>
                  
                  <div className="grid gap-6 max-w-xl">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Aliquota IVA (%)</label>
                        <input
                          type="number"
                          value={settings.vat_rate || 22}
                          onChange={(e) => setSettings({...settings, vat_rate: parseFloat(e.target.value)})}
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">I prezzi lordi saranno ricalcolati</p>
                      </div>
                      
                      <div>
                        <label className="block text-sm text-gray-400 mb-2">Sconto Bundle (%)</label>
                        <input
                          type="number"
                          value={settings.bundle_discount_percent || 5}
                          onChange={(e) => setSettings({...settings, bundle_discount_percent: parseFloat(e.target.value)})}
                          className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                        />
                        <p className="text-xs text-gray-500 mt-1">Sconto per il bundle completo</p>
                      </div>
                    </div>
                    
                    <div>
                      <label className="block text-sm text-gray-400 mb-2">Email Notifiche</label>
                      <input
                        type="email"
                        value={settings.notification_email || ''}
                        onChange={(e) => setSettings({...settings, notification_email: e.target.value})}
                        className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                        placeholder="email@esempio.com"
                      />
                    </div>

                    {/* Policy Links Section */}
                    <div className="border-t border-gray-700 pt-6 mt-2">
                      <h3 className="text-lg font-semibold text-white mb-4">Link Policy e Documenti Legali</h3>
                      
                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Link Privacy Policy</label>
                          <input
                            type="url"
                            value={settings.privacy_policy_url || ''}
                            onChange={(e) => setSettings({...settings, privacy_policy_url: e.target.value})}
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                            placeholder="https://www.tuosito.it/privacy-policy"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Link Condizioni di Servizio VA-PT-CR</label>
                          <input
                            type="url"
                            value={settings.terms_conditions_url || ''}
                            onChange={(e) => setSettings({...settings, terms_conditions_url: e.target.value})}
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                            placeholder="https://www.tuosito.it/condizioni-generali"
                          />
                        </div>
                        
                        <div>
                          <label className="block text-sm text-gray-400 mb-2">Link Manleva</label>
                          <input
                            type="url"
                            value={settings.manleva_url || ''}
                            onChange={(e) => setSettings({...settings, manleva_url: e.target.value})}
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                            placeholder="https://www.tuosito.it/manleva"
                          />
                        </div>
                        
                        {/* Option to combine Terms and Manleva */}
                        <div className="flex items-center gap-3 p-3 bg-gray-800/50 rounded-lg">
                          <input
                            type="checkbox"
                            id="combineTermsManleva"
                            checked={settings.combine_terms_manleva || false}
                            onChange={(e) => setSettings({...settings, combine_terms_manleva: e.target.checked})}
                            className="w-4 h-4 text-red-600 bg-gray-900 border-gray-600 rounded focus:ring-red-500"
                          />
                          <label htmlFor="combineTermsManleva" className="text-sm text-gray-300">
                            Condizioni Generali e Manleva sono nello stesso documento
                          </label>
                        </div>
                        <p className="text-xs text-gray-500">
                          {settings.combine_terms_manleva 
                            ? "Nel form di ordine apparirà un'unica checkbox per 'Condizioni Generali e Manleva'" 
                            : "Nel form di ordine appariranno checkbox separate per ogni documento"}
                        </p>
                        
                        {/* Service Terms for Homepage */}
                        <div className="border-t border-gray-700 pt-4 mt-4">
                          <label className="block text-sm text-gray-400 mb-2">
                            Link Condizioni di Servizio Webcheck
                          </label>
                          <input
                            type="url"
                            value={settings.service_terms_url || ''}
                            onChange={(e) => setSettings({...settings, service_terms_url: e.target.value})}
                            className="w-full px-4 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                            placeholder="https://www.tuosito.it/condizioni-servizio-webcheck"
                          />
                          <p className="text-xs text-gray-500 mt-1">
                            Questo link apparirà nel footer della homepage. Lascia vuoto per nasconderlo.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <button
                      onClick={handleSaveSettings}
                      className="px-6 py-3 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center justify-center gap-2"
                    >
                      <Save className="h-4 w-4" /> Salva Impostazioni
                    </button>
                  </div>
                </div>
              )}

              {/* Users Tab */}
              {activeTab === 'users' && (
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <h2 className="text-xl font-bold text-white">Utenti Admin</h2>
                    <button
                      onClick={() => setShowNewUser(true)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg flex items-center gap-2"
                    >
                      <Plus className="h-4 w-4" /> Nuovo Utente
                    </button>
                  </div>
                  
                  <div className="grid gap-3">
                    {users.map(user => (
                      <div
                        key={user.username}
                        className={`bg-[#1a1a2e] rounded-xl p-4 border ${user.active !== false ? 'border-gray-800' : 'border-red-500/30 opacity-60'}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-4">
                            <div className="p-2 bg-cyan-500/20 rounded-lg">
                              <Users className="h-5 w-5 text-cyan-400" />
                            </div>
                            <div>
                              <h3 className="font-bold text-white">{user.username}</h3>
                              <p className="text-sm text-gray-400">{user.email || 'Nessuna email'}</p>
                            </div>
                            <span className="px-2 py-1 bg-purple-500/20 text-purple-400 text-xs rounded">
                              {user.role || 'admin'}
                            </span>
                          </div>
                          <div className="flex items-center gap-4">
                            {user.last_login && (
                              <span className="text-xs text-gray-500">
                                Ultimo accesso: {new Date(user.last_login).toLocaleString('it-IT')}
                              </span>
                            )}
                            {user.active === false && (
                              <span className="px-2 py-1 bg-red-500/20 text-red-400 text-xs rounded">DISATTIVO</span>
                            )}
                            <div className="flex gap-2">
                              <button
                                onClick={() => setEditingUser(user)}
                                className="p-2 bg-gray-800 hover:bg-gray-700 rounded-lg"
                              >
                                <Edit className="h-4 w-4 text-cyan-400" />
                              </button>
                              {user.username !== currentUser?.username && (
                                <button
                                  onClick={() => handleDeleteUser(user.username)}
                                  className="p-2 bg-gray-800 hover:bg-red-900 rounded-lg"
                                >
                                  <Trash2 className="h-4 w-4 text-red-400" />
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Stats Tab */}
              {activeTab === 'stats' && stats && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-[#1a1a2e] rounded-xl p-6 border border-gray-800">
                      <div className="text-3xl font-bold text-white">{stats.total_orders}</div>
                      <div className="text-sm text-gray-400">Ordini Totali</div>
                    </div>
                    <div className="bg-[#1a1a2e] rounded-xl p-6 border border-gray-800">
                      <div className="text-3xl font-bold text-green-400">{stats.paid_orders}</div>
                      <div className="text-sm text-gray-400">Ordini Pagati</div>
                    </div>
                    <div className="bg-[#1a1a2e] rounded-xl p-6 border border-gray-800">
                      <div className="text-3xl font-bold text-yellow-400">{stats.pending_orders}</div>
                      <div className="text-sm text-gray-400">In Attesa</div>
                    </div>
                    <div className="bg-[#1a1a2e] rounded-xl p-6 border border-gray-800">
                      <div className="text-3xl font-bold text-cyan-400">€{stats.total_revenue}</div>
                      <div className="text-sm text-gray-400">Ricavi Totali</div>
                    </div>
                  </div>
                  
                  {stats.by_service && stats.by_service.length > 0 && (
                    <div className="bg-[#1a1a2e] rounded-xl p-6 border border-gray-800">
                      <h3 className="text-lg font-bold text-white mb-4">Vendite per Servizio</h3>
                      <div className="space-y-3">
                        {stats.by_service.map(s => (
                          <div key={s._id} className="flex items-center justify-between py-2 border-b border-gray-800">
                            <span className="text-gray-300">{s._id}</span>
                            <div className="flex gap-6">
                              <span className="text-white">{s.count} ordini</span>
                              <span className="text-green-400">€{s.revenue}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      {/* Service Edit Modal */}
      {(editingService || showNewService) && (
        <ServiceEditModal
          service={editingService}
          isNew={showNewService}
          onSave={(s) => handleSaveService(s, showNewService)}
          onClose={() => { setEditingService(null); setShowNewService(false); }}
          iconOptions={iconOptions}
          colorOptions={colorOptions}
        />
      )}

      {/* Coupon Edit Modal */}
      {(editingCoupon || showNewCoupon) && (
        <CouponEditModal
          coupon={editingCoupon}
          isNew={showNewCoupon}
          onSave={(c) => handleSaveCoupon(c, showNewCoupon)}
          onClose={() => { setEditingCoupon(null); setShowNewCoupon(false); }}
        />
      )}

      {/* User Edit Modal */}
      {(editingUser || showNewUser) && (
        <UserEditModal
          user={editingUser}
          isNew={showNewUser}
          onSave={(u) => handleSaveUser(u, showNewUser)}
          onClose={() => { setEditingUser(null); setShowNewUser(false); }}
        />
      )}
    </div>
  );
};

// Service Edit Modal Component
const ServiceEditModal = ({ service, isNew, onSave, onClose, iconOptions, colorOptions }) => {
  const [form, setForm] = useState(service || {
    id: '',
    name: '',
    description: '',
    features: [''],
    price_net: 0,
    vat_rate: 22,
    delivery_time: '24 ore',
    icon: 'Shield',
    color: 'from-gray-500 to-gray-600',
    is_bundle: false,
    active: true,
    order: 99
  });

  const handleFeatureChange = (index, value) => {
    const newFeatures = [...form.features];
    newFeatures[index] = value;
    setForm({...form, features: newFeatures});
  };

  const addFeature = () => {
    setForm({...form, features: [...form.features, '']});
  };

  const removeFeature = (index) => {
    setForm({...form, features: form.features.filter((_, i) => i !== index)});
  };

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a2e] rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {isNew ? 'Nuovo Servizio' : 'Modifica Servizio'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">ID (univoco)</label>
              <input
                type="text"
                value={form.id}
                onChange={(e) => setForm({...form, id: e.target.value.toLowerCase().replace(/\s/g, '_')})}
                disabled={!isNew}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white disabled:opacity-50"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Ordine visualizzazione</label>
              <input
                type="number"
                value={form.order}
                onChange={(e) => setForm({...form, order: parseInt(e.target.value)})}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">Nome</label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setForm({...form, name: e.target.value})}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">Descrizione</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              rows={2}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Prezzo Netto (€)</label>
              <input
                type="number"
                value={form.price_net}
                onChange={(e) => setForm({...form, price_net: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              />
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">IVA (%)</label>
              <input
                type="number"
                value={form.vat_rate}
                onChange={(e) => setForm({...form, vat_rate: parseFloat(e.target.value)})}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              />
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-400 mb-1">Icona</label>
              <select
                value={form.icon}
                onChange={(e) => setForm({...form, icon: e.target.value})}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              >
                {iconOptions.map(icon => (
                  <option key={icon} value={icon}>{icon}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm text-gray-400 mb-1">Colore</label>
              <select
                value={form.color}
                onChange={(e) => setForm({...form, color: e.target.value})}
                className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              >
                {colorOptions.map(color => (
                  <option key={color} value={color}>{color.replace('from-', '').replace(' to-', ' → ')}</option>
                ))}
              </select>
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">Caratteristiche</label>
            {form.features.map((feature, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="text"
                  value={feature}
                  onChange={(e) => handleFeatureChange(idx, e.target.value)}
                  className="flex-1 px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
                />
                <button
                  onClick={() => removeFeature(idx)}
                  className="p-2 bg-red-500/20 hover:bg-red-500/30 rounded-lg"
                >
                  <X className="h-4 w-4 text-red-400" />
                </button>
              </div>
            ))}
            <button
              onClick={addFeature}
              className="text-sm text-cyan-400 hover:text-cyan-300"
            >
              + Aggiungi caratteristica
            </button>
          </div>
          
          <div className="flex items-center gap-4">
            <label className="flex items-center gap-2 text-gray-300">
              <input
                type="checkbox"
                checked={form.is_bundle}
                onChange={(e) => setForm({...form, is_bundle: e.target.checked})}
                className="rounded"
              />
              È un Bundle
            </label>
            <label className="flex items-center gap-2 text-gray-300">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(e) => setForm({...form, active: e.target.checked})}
                className="rounded"
              />
              Attivo
            </label>
          </div>
        </div>
        
        <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
          >
            Annulla
          </button>
          <button
            onClick={() => onSave(form)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
};

// Coupon Edit Modal Component
const CouponEditModal = ({ coupon, isNew, onSave, onClose }) => {
  const [form, setForm] = useState(coupon || {
    code: '',
    discount_percent: 10,
    description: '',
    active: true
  });

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a2e] rounded-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {isNew ? 'Nuovo Coupon' : 'Modifica Coupon'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Codice</label>
            <input
              type="text"
              value={form.code}
              onChange={(e) => setForm({...form, code: e.target.value.toUpperCase()})}
              disabled={!isNew}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white font-mono disabled:opacity-50"
              placeholder="ES: SCONTO20"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">Sconto (%)</label>
            <input
              type="number"
              value={form.discount_percent}
              onChange={(e) => setForm({...form, discount_percent: parseFloat(e.target.value)})}
              min={1}
              max={100}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">Descrizione</label>
            <input
              type="text"
              value={form.description}
              onChange={(e) => setForm({...form, description: e.target.value})}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              placeholder="Es: Sconto speciale 20%"
            />
          </div>
          
          <label className="flex items-center gap-2 text-gray-300">
            <input
              type="checkbox"
              checked={form.active}
              onChange={(e) => setForm({...form, active: e.target.checked})}
              className="rounded"
            />
            Attivo
          </label>
        </div>
        
        <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
          >
            Annulla
          </button>
          <button
            onClick={() => onSave(form)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
};

// User Edit Modal Component
const UserEditModal = ({ user, isNew, onSave, onClose }) => {
  const [form, setForm] = useState(user || {
    username: '',
    password: '',
    email: '',
    role: 'admin',
    active: true
  });
  const [showPassword, setShowPassword] = useState(false);

  return (
    <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
      <div className="bg-[#1a1a2e] rounded-xl max-w-md w-full">
        <div className="p-6 border-b border-gray-800 flex items-center justify-between">
          <h2 className="text-xl font-bold text-white">
            {isNew ? 'Nuovo Utente' : 'Modifica Utente'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-800 rounded-lg">
            <X className="h-5 w-5 text-gray-400" />
          </button>
        </div>
        
        <div className="p-6 space-y-4">
          <div>
            <label className="block text-sm text-gray-400 mb-1">Username</label>
            <input
              type="text"
              value={form.username}
              onChange={(e) => setForm({...form, username: e.target.value})}
              disabled={!isNew}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white disabled:opacity-50"
              placeholder="nome_utente"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">
              {isNew ? 'Password' : 'Nuova Password (lascia vuoto per non cambiare)'}
            </label>
            <div className="relative">
              <input
                type={showPassword ? 'text' : 'password'}
                value={form.password || ''}
                onChange={(e) => setForm({...form, password: e.target.value})}
                className="w-full px-3 py-2 pr-10 bg-gray-900 border border-gray-700 rounded-lg text-white"
                placeholder={isNew ? 'Minimo 6 caratteri' : '••••••'}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-300"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">Email</label>
            <input
              type="email"
              value={form.email || ''}
              onChange={(e) => setForm({...form, email: e.target.value})}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
              placeholder="email@esempio.com"
            />
          </div>
          
          <div>
            <label className="block text-sm text-gray-400 mb-1">Ruolo</label>
            <select
              value={form.role || 'admin'}
              onChange={(e) => setForm({...form, role: e.target.value})}
              className="w-full px-3 py-2 bg-gray-900 border border-gray-700 rounded-lg text-white"
            >
              <option value="admin">Admin</option>
              <option value="editor">Editor</option>
              <option value="viewer">Viewer</option>
            </select>
          </div>
          
          {!isNew && (
            <label className="flex items-center gap-2 text-gray-300">
              <input
                type="checkbox"
                checked={form.active !== false}
                onChange={(e) => setForm({...form, active: e.target.checked})}
                className="rounded"
              />
              Attivo
            </label>
          )}
        </div>
        
        <div className="p-6 border-t border-gray-800 flex justify-end gap-3">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-800 hover:bg-gray-700 text-white rounded-lg"
          >
            Annulla
          </button>
          <button
            onClick={() => onSave(form)}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg flex items-center gap-2"
          >
            <Save className="h-4 w-4" /> Salva
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
